import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { Box, TextField, Button, InputAdornment, Tooltip, Typography } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import InfoIcon from "@mui/icons-material/Info";
import SearchUtils from "../utils/SearchUtils";

const SEARCH_DEBOUNCE_MS = 300;

const CounterSearchInput = ({
  t,
  prescriptionAssign,
  searchText,
  searchInputResetKey,
  onChange,
  onVerifyPatient,
  onEnter,
  strictPatientVerification = false,
  enableTrim = false,
  trimChars = SearchUtils.defaultTrimChars,
}) => {
  const [localValue, setLocalValue] = useState(searchText || "");
  const debounceTimerRef = useRef(null);

  // Sync local value when parent clears or sets searchText (e.g. after shortcut)
  useEffect(() => {
    setLocalValue(searchText || "");
  }, [searchText]);

  useEffect(() => {
    setLocalValue("");
  }, [searchInputResetKey]);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const handleChange = (value) => {
    setLocalValue(value);
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    debounceTimerRef.current = setTimeout(() => {
      debounceTimerRef.current = null;
      const trimmedValue = enableTrim
        ? SearchUtils.trimLeadingTrailing(value, trimChars)
        : value;
      onChange(trimmedValue);
    }, SEARCH_DEBOUNCE_MS);
  };

  const placeholder = strictPatientVerification
    ? t("Console.Counter.ScanQRPlaceholder", {
      defaultValue: "Scan QR code",
    })
    : t("Console.Counter.SearchPlaceholder", {
      defaultValue: "Search by Pickup Code or Name",
    });

  const tooltipText = strictPatientVerification
    ? t("Console.Counter.StrictModeSearchTooltip", {
      defaultValue:
        "Strict mode enabled: Scan the QR code from patient's phone (located in pickup code screen) or verify the patient to access orders",
    })
    : "";

  // Custom tooltip title with icon for strict mode
  const tooltipTitle = strictPatientVerification ? (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 1.5,
        py: 0.5,
      }}
    >
      <InfoIcon
        sx={{
          color: "#ffffff",
          fontSize: "1.25rem",
          mt: 0.25,
          flexShrink: 0,
        }}
      />
      <Typography
        component="span"
        sx={{
          fontSize: "0.875rem",
          lineHeight: 1.6,
          color: "#ffffff",
          fontWeight: 500,
        }}
      >
        {tooltipText}
      </Typography>
    </Box>
  ) : (
    tooltipText
  );

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && localValue.trim() && onVerifyPatient) {
      e.preventDefault();
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      // Trim backslashes (and other configured chars) so \4YUJK9\ → 4YUJK9
      const trimmedValue = enableTrim
        ? SearchUtils.trimLeadingTrailing(localValue, trimChars)
        : SearchUtils.trimLeadingTrailingSlashesAndSpaces(localValue);
      // Update the visible field to the trimmed value
      setLocalValue(trimmedValue);
      // Propagate trimmed value to parent for order lookup
      onChange(trimmedValue);

      if (onEnter) {
        onEnter();
      }
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        mb: 3,
        flexWrap: "wrap",
      }}
    >
      <Box sx={{ flex: 1, minWidth: { xs: "100%", sm: "60%" }, position: "relative" }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder={placeholder}
          value={localValue}
          onChange={(event) => handleChange(event.target.value)}
          onKeyDown={handleKeyDown}
          disabled={!prescriptionAssign}
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon
                  sx={{
                    color: prescriptionAssign
                      ? strictPatientVerification
                        ? "#f59e0b"
                        : "#7c3aed"
                      : "#b0bec5",
                    fontSize: 22,
                  }}
                />
              </InputAdornment>
            ),
            endAdornment: strictPatientVerification ? (
              <InputAdornment position="end">
                <Tooltip
                  title={tooltipTitle}
                  arrow
                  placement="top"
                  componentsProps={{
                    tooltip: {
                      sx: {
                        maxWidth: 420,
                        background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                        fontSize: "0.875rem",
                        lineHeight: 1.5,
                        padding: "12px 16px",
                        borderRadius: 2,
                        boxShadow: "0 8px 24px rgba(245, 158, 11, 0.4)",
                        border: "1px solid rgba(255, 255, 255, 0.2)",
                        "& .MuiTooltip-arrow": {
                          color: "#f59e0b",
                        },
                      },
                    },
                  }}
                >
                  <InfoIcon
                    sx={{
                      color: "#f59e0b",
                      fontSize: 22,
                      cursor: "help",
                      "&:hover": {
                        color: "#d97706",
                      },
                    }}
                  />
                </Tooltip>
              </InputAdornment>
            ) : null,
          }}
          sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                backgroundColor: "#ffffff",
                transition: "all 0.3s ease",
                "&:hover": {
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: strictPatientVerification ? "#d97706" : "#7c3aed",
                },
              },
              "&.Mui-focused": {
                boxShadow: strictPatientVerification
                  ? "0 0 0 3px rgba(245, 158, 11, 0.1)"
                  : "0 0 0 3px rgba(124, 58, 237, 0.1)",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: strictPatientVerification ? "#d97706" : "#7c3aed",
                  borderWidth: 2,
                },
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: strictPatientVerification ? "#f59e0b" : "#e2e8f0",
                borderWidth: 2,
              },
            },
          }}
        />
      </Box>
      {onVerifyPatient ? (
        <Button
          variant="contained"
          onClick={onVerifyPatient}
          size="medium"
          sx={{
            textTransform: "none",
            fontWeight: 600,
            backgroundColor: "#7c3aed",
            color: "#ffffff",
            px: 2.75,
            py: 1,
            borderRadius: 2,
            boxShadow: "0 6px 18px rgba(124, 58, 237, 0.25)",
            "&:hover": {
              backgroundColor: "#6d28d9",
              boxShadow: "0 8px 22px rgba(124, 58, 237, 0.35)",
            },
          }}
        >
          {t("Console.Counter.VerifyPatientButton", {
            defaultValue: "Verify patient",
          })}
        </Button>
      ) : null}
    </Box>
  );
};

CounterSearchInput.propTypes = {
  t: PropTypes.func.isRequired,
  prescriptionAssign: PropTypes.bool,
  searchText: PropTypes.string,
  searchInputResetKey: PropTypes.number,
  onChange: PropTypes.func.isRequired,
  onVerifyPatient: PropTypes.func,
  onEnter: PropTypes.func,
  strictPatientVerification: PropTypes.bool,
  enableTrim: PropTypes.bool,
  trimChars: PropTypes.string,
};

CounterSearchInput.defaultProps = {
  prescriptionAssign: true,
  searchText: "",
  searchInputResetKey: 0,
  onVerifyPatient: undefined,
  onEnter: undefined,
  strictPatientVerification: false,
  enableTrim: false,
  trimChars: SearchUtils.defaultTrimChars,
};

export default CounterSearchInput;



// WEBPACK FOOTER //
// ./src/components/assign-prescriptions/counter/components/CounterSearchInput.jsx